import React, { useState, useRef, useEffect } from 'react';
import { openaiService } from '../services/openaiService';
import { FEATURES } from '../utils/env';
import { toast } from '../utils/toast';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../contexts/AuthContext';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { PageHeader } from '../components/PageHeader';
import { Layout } from '../components/Layout';
import { googleSpeechService } from '../services/googleSpeechService';

// Message types enum
const MessageTypes = {
  TEXT: 'text',
  CODE: 'code',
  IMAGE: 'image',
  FILE: 'file'
};

// Modern message component inspired by Claude/ChatGPT
const ChatMessage = ({ message, isUser }) => {
  const bubbleVariants = {
    hidden: { 
      opacity: 0,
      y: 10,
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <motion.div
      variants={bubbleVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={`py-5 ${!isUser ? 'bg-gray-50' : 'bg-white'} border-b border-gray-100`}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex items-start">
        <div className={`w-8 h-8 mt-1 rounded-full flex items-center justify-center flex-shrink-0 mr-4
          ${isUser ? 'bg-blue-100 text-blue-600' : 'bg-gray-700 text-white'}`}>
          {isUser ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          )}
        </div>
        
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-700 mb-1.5">
            {isUser ? 'You' : 'AI Assistant'}
          </div>
          {message.type === MessageTypes.CODE ? (
            <div className="overflow-x-auto rounded-md">
              <SyntaxHighlighter
                language={message.language || 'javascript'}
                style={atomDark}
                customStyle={{
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                {message.content}
              </SyntaxHighlighter>
            </div>
          ) : message.type === MessageTypes.IMAGE ? (
            <img
              src={message.content}
              alt="AI Generated"
              className="rounded-lg max-w-full h-auto"
              loading="lazy"
            />
          ) : (
            <ReactMarkdown
              className={`prose prose-slate max-w-none text-base prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2`}
              components={{
                code: ({node, inline, className, children, ...props}) => {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      language={match[1]}
                      style={atomDark}
                      PreTag="div"
                      customStyle={{
                        margin: '0.5rem 0',
                        borderRadius: '0.375rem'
                      }}
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={`${className} bg-gray-100 px-1.5 py-0.5 rounded text-gray-800`} {...props}>
                      {children}
                    </code>
                  )
                }
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const TypingIndicator = () => (
  <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-4 flex items-center">
    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-700 text-white mr-4">
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    </div>
    <div>
      <div className="text-sm font-medium mb-1">AI Assistant</div>
      <div className="flex space-x-1.5 items-center">
        <motion.div
          className="w-2 h-2 bg-primary-400 rounded-full"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.4, repeat: Infinity }}
        />
        <motion.div
          className="w-2 h-2 bg-primary-400 rounded-full"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.4, delay: 0.2, repeat: Infinity }}
        />
        <motion.div
          className="w-2 h-2 bg-primary-400 rounded-full"
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.4, delay: 0.4, repeat: Infinity }}
        />
      </div>
    </div>
  </div>
);

// Conversation item component for the sidebar
const ConversationItem = ({ title, isActive, onClick, onDelete }) => (
  <div 
    className={`group flex items-center p-3 text-sm rounded-lg cursor-pointer transition-all ${
      isActive ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'
    }`}
    onClick={onClick}
  >
    <svg className="w-4 h-4 mr-3 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>
    <span className="flex-1 truncate">{title}</span>
    <button 
      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded-full transition-opacity"
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
    >
      <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>
  </div>
);

const VoiceRecorderButton = ({ onTranscriptionComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      // Request microphone access with specific constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          sampleSize: 16,
          volume: 1.0
        }
      });
      
      streamRef.current = stream;
      
      // Check if the browser supports the required mime type
      const mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        throw new Error('Browser does not support the required audio format');
      }

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000
      });
      
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        setIsProcessing(true);
        try {
          if (chunksRef.current.length === 0) {
            throw new Error('No audio data recorded');
          }

          const audioBlob = new Blob(chunksRef.current, { type: mimeType });
          
          const transcription = await googleSpeechService.transcribeAudio(audioBlob);
          
          if (transcription && transcription.trim()) {
            onTranscriptionComplete(transcription);
            toast.success('Audio transcribed successfully!');
          } else {
            throw new Error('No transcription received');
          }
        } catch (error) {
          console.error('Recording error:', error);
          toast.error(error.message || 'Failed to transcribe audio');
        } finally {
          setIsProcessing(false);
          // Clean up the media stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
        }
      };

      // Start recording with 1-second intervals for data
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      toast.info('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error(error.message || 'Failed to start recording');
      // Clean up if there was an error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        toast.info('Recording stopped');
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
      toast.error('Failed to stop recording');
      setIsRecording(false);
    }
  };

  return (
    <button
      onClick={isRecording ? stopRecording : startRecording}
      disabled={isProcessing}
      className={`p-1.5 rounded-lg transition-colors relative ${
        isRecording
          ? 'bg-red-100 text-red-600 animate-pulse'
          : isProcessing
          ? 'bg-gray-100 text-gray-400 cursor-wait'
          : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
      }`}
      title={isRecording ? "Stop recording" : "Start voice recording"}
    >
      {isRecording ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <rect x="6" y="6" width="12" height="12" rx="1" strokeWidth={2} />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      )}
      {isProcessing && (
        <div className="absolute -top-1 -right-1 w-3 h-3">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
        </div>
      )}
    </button>
  );
};

export function AIToybox() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([
    { 
      id: 'default',
      title: 'New conversation',
      messages: [
        {
          id: '1',
          type: MessageTypes.TEXT,
          content: "Hello! I'm your AI assistant. I can help you with writing, analysis, coding, and more. What would you like to explore today?",
          isUser: false
        }
      ]
    }
  ]);
  const [activeConversationId, setActiveConversationId] = useState('default');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const chatContainerRef = useRef(null);
  
  // Get active conversation
  const activeConversation = conversations.find(c => c.id === activeConversationId) || conversations[0];
  const messages = activeConversation.messages;

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const createNewConversation = () => {
    const newId = Date.now().toString();
    const newConversation = {
      id: newId,
      title: 'New conversation',
      messages: [
        {
          id: '1',
          type: MessageTypes.TEXT,
          content: "Hello! I'm your AI assistant. I can help you with writing, analysis, coding, and more. What would you like to explore today?",
          isUser: false
        }
      ]
    };
    
    setConversations(prev => [newConversation, ...prev]);
    setActiveConversationId(newId);
    setInput('');
  };

  const updateConversationTitle = (conversationId, firstUserMessage) => {
    // Generate a title from the first user message (limit to first 30 chars)
    const title = firstUserMessage.length > 30 
      ? firstUserMessage.substring(0, 30) + '...'
      : firstUserMessage;
      
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, title } 
          : conv
      )
    );
  };

  const deleteConversation = (conversationId) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    
    // If we're deleting the active conversation, switch to the first available
    if (conversationId === activeConversationId) {
      const remainingConversations = conversations.filter(conv => conv.id !== conversationId);
      if (remainingConversations.length > 0) {
        setActiveConversationId(remainingConversations[0].id);
      } else {
        // If no conversations left, create a new one
        createNewConversation();
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isProcessing) return;

    const userMessage = {
      id: Date.now().toString(),
      type: MessageTypes.TEXT,
      content: input.trim(),
      isUser: true
    };

    // Update conversation with user message
    const updatedConversations = conversations.map(conv => 
      conv.id === activeConversationId
        ? { ...conv, messages: [...conv.messages, userMessage] }
        : conv
    );
    setConversations(updatedConversations);
    
    // Update the title with the first user message
    if (messages.length === 1) { // Only welcome message present
      updateConversationTitle(activeConversationId, input.trim());
    }
    
    setInput('');
    setIsProcessing(true);

    try {
      // Get the updated messages for API call
      const currentMessages = updatedConversations.find(c => c.id === activeConversationId).messages;
      const response = await openaiService.chat(currentMessages);
      
      if (response && response.text) {
        const aiMessage = {
          id: (Date.now() + 1).toString(),
          type: MessageTypes.TEXT,
          content: response.text,
          isUser: false
        };
        
        setConversations(prev => 
          prev.map(conv => 
            conv.id === activeConversationId
              ? { ...conv, messages: [...conv.messages, aiMessage] }
              : conv
          )
        );
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get response. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Please upload a file smaller than 5MB.');
      return;
    }
    
    // User message to show file upload
    const userMessage = {
      id: Date.now().toString(),
      type: MessageTypes.TEXT,
      content: `Uploaded file: ${file.name}`,
      isUser: true
    };
    
    // Add user message to conversation
    setConversations(prev => 
      prev.map(conv => 
        conv.id === activeConversationId
          ? { ...conv, messages: [...conv.messages, userMessage] }
          : conv
      )
    );
    
    setIsProcessing(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        
        // For large text, split it into chunks
        if (text.length > 8000) {
          const chunkSize = 8000;
          const chunks = [];
          
          for (let i = 0; i < text.length; i += chunkSize) {
            chunks.push(text.substring(i, i + chunkSize));
          }
          
          // First, send an interim message
          const interimMessage = {
            id: (Date.now() + 1).toString(),
            type: MessageTypes.TEXT,
            content: `Analyzing file ${file.name} (${(text.length / 1024).toFixed(1)}KB)...`,
            isUser: false
          };
          
          setConversations(prev => 
            prev.map(conv => 
              conv.id === activeConversationId
                ? { ...conv, messages: [...conv.messages, interimMessage] }
                : conv
            )
          );
          
          // Process each chunk
          let combinedAnalysis = `# Analysis of ${file.name}\n\n`;
          
          for (let i = 0; i < chunks.length; i++) {
            const response = await openaiService.analyzeDocument(
              `This is part ${i+1} of ${chunks.length} of the document: ${chunks[i]}`
            );
            combinedAnalysis += response + '\n\n';
          }
          
          // Replace the interim message with the final analysis
          const currentMessages = conversations.find(c => c.id === activeConversationId).messages;
          const updatedMessages = [...currentMessages.slice(0, -1)];
          
          const finalMessage = {
            id: (Date.now() + 1).toString(),
            type: MessageTypes.TEXT,
            content: combinedAnalysis,
            isUser: false
          };
          
          setConversations(prev => 
            prev.map(conv => 
              conv.id === activeConversationId
                ? { ...conv, messages: [...updatedMessages, finalMessage] }
                : conv
            )
          );
        } else {
          // For small files, process normally
          const response = await openaiService.analyzeDocument(text);
          
          const aiMessage = {
            id: (Date.now() + 1).toString(),
            type: MessageTypes.TEXT,
            content: response,
            isUser: false
          };
          
          setConversations(prev => 
            prev.map(conv => 
              conv.id === activeConversationId
                ? { ...conv, messages: [...conv.messages, aiMessage] }
                : conv
            )
          );
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('File analysis error:', error);
      const errorMessage = error.message.includes('429') 
        ? 'Rate limit exceeded. Please wait a moment before uploading another file.'
        : 'Failed to analyze file. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVoiceRecord = async (userMessage) => {
    // Add user message to conversation
    setConversations(prev => 
      prev.map(conv => 
        conv.id === activeConversationId
          ? { ...conv, messages: [...conv.messages, userMessage] }
          : conv
      )
    );
    
    setIsProcessing(true);

    try {
      // Get the updated messages for API call
      const currentMessages = conversations.find(c => c.id === activeConversationId).messages;
      const updatedMessages = [...currentMessages, userMessage];
      
      const response = await openaiService.chat(updatedMessages);
      
      if (response && response.text) {
        const aiMessage = {
          id: (Date.now() + 1).toString(),
          type: MessageTypes.TEXT,
          content: response.text,
          isUser: false
        };
        
        setConversations(prev => 
          prev.map(conv => 
            conv.id === activeConversationId
              ? { ...conv, messages: [...conv.messages, aiMessage] }
              : conv
          )
        );
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get response. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="h-screen max-h-screen flex flex-col overflow-hidden bg-white pl-0 md:pl-16">
      <div className="flex flex-1 min-h-0">
        {/* Sidebar with conversation history */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-72 border-r border-gray-200 h-full flex flex-col z-10 overflow-hidden"
            >
              <div className="p-3 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={createNewConversation}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>New conversation</span>
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2 min-h-0">
                {conversations.map(conversation => (
                  <ConversationItem
                    key={conversation.id}
                    title={conversation.title}
                    isActive={conversation.id === activeConversationId}
                    onClick={() => setActiveConversationId(conversation.id)}
                    onDelete={() => deleteConversation(conversation.id)}
                  />
                ))}
              </div>
              
              <div className="p-3 border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">{user?.email || 'User'}</div>
                    <div className="text-xs text-gray-500">Free plan · 100 messages/day</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <div className="border-b border-gray-200 bg-white py-2.5 px-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="mr-3 p-1.5 rounded hover:bg-gray-100 transition-colors"
                aria-label="Toggle conversation sidebar"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-medium text-gray-800 truncate">
                {activeConversation.title}
              </h1>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                <span className="text-xs font-medium">Online</span>
              </div>
            </div>
          </div>
          
          {/* Messages area */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto min-h-0"
          >
            <AnimatePresence initial={false}>
              {messages.map(message => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  isUser={message.isUser}
                />
              ))}
            </AnimatePresence>
            
            {isProcessing && <TypingIndicator />}
          </div>
          
          {/* Input area */}
          <div className="border-t border-gray-200 p-4 bg-white flex-shrink-0">
            <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
              <div className="relative rounded-xl border border-gray-300 bg-white shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Send a message..."
                  rows={1}
                  className="block w-full resize-none border-0 bg-transparent py-3 px-4 focus:outline-none focus:ring-0 text-base"
                  style={{ maxHeight: '200px', minHeight: '56px' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      onClick={() => document.getElementById('file-upload').click()}
                      title="Attach file"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <input
                        id="file-upload"
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                        accept=".txt,.pdf,.doc,.docx,.csv"
                      />
                    </button>
                    
                    <VoiceRecorderButton 
                      onTranscriptionComplete={(transcribedText) => {
                        const userMessage = {
                          id: Date.now().toString(),
                          type: MessageTypes.TEXT,
                          content: transcribedText,
                          isUser: true
                        };
                        handleVoiceRecord(userMessage);
                      }}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isProcessing || !input.trim()}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isProcessing || !input.trim()
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:bg-blue-50'
                    }`}
                    title="Send message"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
} 